import { Card, Text } from "@clipboard-health/ui-components";
import { Box, CardContent, Stack, type Theme } from "@mui/material";
import { ShieldIcon } from "@src/appV2/redesign/EmergencyCredits/components/ShieldIcon";
import { ShieldWithSlashIcon } from "@src/appV2/redesign/EmergencyCredits/components/ShieldWithSlashIcon";

const ICON_CIRCLE_SIZE = 24;
const ICON_SIZE = "0.875rem";

interface CancellationCreditPenaltySubCardProps {
  title: string;
  pointsWithCredit: number;
  pointsWithoutCredit: number;
  hasCredit: boolean;
}

interface PenaltyRowProps {
  label: string;
  points: number;
  isActive: boolean;
  isNegative: boolean;
}

function IconCircle({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={(theme) => ({
        width: ICON_CIRCLE_SIZE,
        height: ICON_CIRCLE_SIZE,
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: "50%",
        backgroundColor: theme.palette.background.primary,
      })}
    >
      {children}
    </Box>
  );
}

function getLabelColor(theme: Theme, isActive: boolean, isNegative: boolean): string | undefined {
  if (!isActive) {
    return theme.palette.grey[400];
  }

  return isNegative ? theme.palette.intent?.error.text : theme.palette.text.primary;
}

function PenaltyRow(props: PenaltyRowProps) {
  const { label, points, isActive, isNegative } = props;

  const inactiveColor = (theme: Theme) => theme.palette.grey[400];

  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Stack direction="row" alignItems="center" spacing={2}>
        <IconCircle>
          {isNegative ? (
            <ShieldWithSlashIcon
              sx={(theme) => ({
                fontSize: ICON_SIZE,
                color: inactiveColor(theme),
              })}
            />
          ) : (
            <ShieldIcon
              variant="outlined"
              sx={{
                fontSize: ICON_SIZE,
              }}
            />
          )}
        </IconCircle>
        <Text
          variant="body2"
          sx={(theme) => ({
            color: getLabelColor(theme, isActive, isNegative),
            textDecoration: isActive ? "none" : "line-through",
          })}
        >
          {label}
        </Text>
      </Stack>
      <Text
        semibold
        variant="body2"
        sx={(theme) => ({
          color: isActive ? theme.palette.intent?.error.text : inactiveColor(theme),
          textDecoration: isActive ? "none" : "line-through",
        })}
      >
        - {Math.abs(points)} pts
      </Text>
    </Stack>
  );
}

export function CancellationCreditPenaltySubCard(props: CancellationCreditPenaltySubCardProps) {
  const { title, pointsWithCredit, pointsWithoutCredit, hasCredit } = props;

  return (
    <Stack direction="column" spacing={3}>
      <Text bold>{title}</Text>
      <Card variant="tertiary">
        <CardContent>
          <Stack spacing={3}>
            <PenaltyRow
              isNegative={false}
              label="With a credit"
              points={pointsWithCredit}
              isActive={hasCredit}
            />
            <PenaltyRow
              isNegative
              label="Without a credit"
              points={pointsWithoutCredit}
              isActive={!hasCredit}
            />
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
}
